import {
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext
} from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import kdbush from 'kdbush'
import geokdbush from 'geokdbush'

import { point, lineString } from '@turf/helpers'
import nearestPointOnLine from '@turf/nearest-point-on-line'
import length from '@turf/length'

import isEqual from 'lodash/isEqual'

import { range } from './utils'
import { lnglat2quadhash } from './quadkeys'

import SettingsContext from './components/SettingsContext'

import { setInfoPanel, addMessage, showSidebar } from './slices/ui'

import { useUserPosition, useMonitorUserPosition } from './slices/userposition'

import {
  ADD_AT_TRAIL_END,
  ADD_AT_TRAIL_START,
  ADD_TO_TRAIL,
  CHECK_AVAILABILITY_PRICE,
  MERGE_STAGE,
  REMOVE_FIRST_STAGE,
  REMOVE_LAST_STAGE,
  REMOVE_START_POINT,
  REMOVE_STOP,
  SET_STAGE_START,
  SET_STAGE_STOP,
  SPLIT_STAGE
} from './trans'

import {
  useRoute,
  useRouteNearestTrail,
  useRouteWaypointConnection,
  useTrailWaypointConnection,
  useTrailProfile,
  useSteps,
  useSetRouteTrailStart,
  useSetRouteTrailStop,
  useAddRouteCheckpoint,
  useRemoveRouteCheckpoint,
  useSplitRouteTrail,
  useCreateTrail
} from './api'

export const useNavigatorOnline = () => {
  const [value, setValue] = useState(window.navigator.onLine)

  useEffect(() => {
    function handleOnlineStatus() {
      setValue(window.navigator.onLine)
    }

    window.addEventListener('online', handleOnlineStatus)
    window.addEventListener('offline', handleOnlineStatus)

    return () => {
      window.removeEventListener('online', handleOnlineStatus)
      window.removeEventListener('offline', handleOnlineStatus)
    }
  }, [])

  return { isOnline: value, isOffline: !value }
}

export const useLocation = () => {
  const history = useHistory()
  const [location, setLocation] = useState([
    history.location.pathname,
    history.location.search
  ])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setLocation([location.pathname, location.search])
    })
    return () => unlisten()
  }, [history])

  return location
}

export const usePathname = () => {
  const history = useHistory()
  const [pathname, setPathname] = useState(history.location.pathname)

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setPathname(location.pathname)
    })
    return () => unlisten()
  }, [history])

  return pathname
}

export const useIsPageVisible = () => {
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden)

  useLayoutEffect(() => {
    const handleVisibility = () => {
      setIsPageVisible(!document.hidden)
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [])

  return isPageVisible
}

export const useIntersect = ({
  root = null,
  rootMargin,
  threshold = 0,
  once = false
}) => {
  const [entry, updateEntry] = useState({})
  const [node, setNode] = useState(null)
  const observer = useRef(null)
  const intersects = entry && entry.isIntersecting

  useEffect(() => {
    if (observer.current) observer.current.disconnect()

    observer.current = new window.IntersectionObserver(
      ([entry]) => updateEntry(entry),
      {
        root,
        rootMargin,
        threshold
      }
    )

    if (node) observer.current.observe(node)
    return () => observer.current.disconnect()
  }, [node, root, rootMargin, threshold, once])

  if (intersects && once) observer.current.disconnect()

  return [entry, setNode]
}

export const useOnViewport = ({ root = null, rootMargin, once = false }) => {
  const [isOnViewPort, updateIsOnViewport] = useState(null)
  const [node, setNode] = useState(null)
  const trigger = useRef(false)
  const observer = useRef(null)

  useEffect(() => {
    if (trigger.current) return
    if (observer.current) observer.current.disconnect()

    observer.current = new window.IntersectionObserver(
      ([entry]) => updateIsOnViewport(entry && entry.isIntersecting),
      {
        root,
        rootMargin
      }
    )

    if (node) observer.current.observe(node)
    return () => observer.current.disconnect()
  }, [node, root, rootMargin, once])

  if (trigger.current) {
    return [true, () => {}]
  }

  if (isOnViewPort && once) {
    observer.current.disconnect()
    trigger.current = true
    return [true, () => {}]
  }

  return [isOnViewPort, setNode]
}

const filterParams = (obj, keys) => {
  if (!keys) return obj
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => keys.includes(key))
  )
}

export const useUrlParams = (keys) => {
  const history = useHistory()
  const prevMatch = useRef()
  const settings = useContext(SettingsContext)
  const matchPathname = useRef(settings.URLS.matchPathname)
  const [match, setMatch] = useState(
    filterParams(matchPathname.current(history.location.pathname), keys)
  )

  useEffect(() => {
    const unlisten = history.listen(() => {
      const m = filterParams(
        matchPathname.current(history.location.pathname),
        keys
      )
      if (!isEqual(m, prevMatch.current)) {
        setMatch(m)
      }
      prevMatch.current = m
    })
    return () => unlisten()
  }, [history, keys])

  return match
}

export const useNavigateToActionResult = (query, options) => {
  const history = useHistory()
  const settings = useContext(SettingsContext)
  const reverse = settings.URLS.reverse
  const [action, result, isLoading] = query(options)
  const dispatch = useDispatch()
  const data = result?.data
  const message = result?.message

  useEffect(() => {
    if (data) {
      dispatch(setInfoPanel(null))
      const reverse_args = Object.assign(
        {},
        (options && options.reverse_defaults) || {},
        data
      )
      const url = reverse(reverse_args.route_id, reverse_args.trail_id)
      history.push(
        {
          pathname: url,
          hash: history.location.hash
        },
        history.location.state
      )
    }
    if (message) {
      dispatch(
        addMessage({
          message: message,
          timeout: 5,
          status: 'info'
        })
      )
    }
  }, [dispatch, data, message, history, options, reverse])

  return [action, isLoading]
}

export const useGetActionResult = (query, args, options) => {
  const [action, result, isLoading] = query(options)
  if (!isLoading && !result && !options.skip) {
    action(args)
  }
  return result?.data
}

export const useSpatialSearch = (profile, lngLat, tollerance) => {
  const spatialIndex = useMemo(
    () =>
      profile
        ? new kdbush(
            profile,
            (p) => p.lnglat[0],
            (p) => p.lnglat[1]
          )
        : null,
    [profile]
  )
  if (!profile || !lngLat || !tollerance) return null
  const pointProfile = geokdbush.around(
    spatialIndex,
    lngLat[0],
    lngLat[1],
    1,
    1
  )[0]
  if (!pointProfile) return null
  const idx = profile.indexOf(pointProfile)
  const points = []
  if (idx > 1) {
    points.push(profile[idx - 1])
  }
  points.push(pointProfile)
  if (idx < profile.length - 1) {
    points.push(profile[idx + 1])
  }
  const line = lineString(points.map((p) => p.lnglat))
  const pointOnLine = nearestPointOnLine(line, point(lngLat))
  if (pointOnLine.properties.dist * 1000 > tollerance) return null
  const lineLength = length(line)
  const distance =
    points[0].distance +
    ((points[points.length - 1].distance - points[0].distance) / lineLength) *
      pointOnLine.properties.location
  const limits =
    points.length === 2 || points[1].distance < distance
      ? [points[0], points[1]]
      : [points[1], points[2]]
  const time =
    limits[0].time +
    ((limits[1].time - limits[0].time) /
      (limits[1].distance - limits[0].distance)) *
      (distance - limits[0].distance)
  const elevation =
    limits[0].elevation +
    ((limits[1].elevation - limits[0].elevation) /
      (limits[1].distance - limits[0].distance)) *
      (distance - limits[0].distance)

  return {
    lnglat: pointOnLine.geometry.coordinates,
    distance: Math.round(distance),
    time: Math.round(time),
    elevation: Math.round(elevation),
    distanceFromTrail: Math.round(pointOnLine.properties.dist * 100) * 10
  }
}

export const useRealtiveTrailConnection = (args, options) => {
  const [userPositon, setUserPosition] = useState()
  const userPositionOnTrail = useUserPosition() || {}
  const monitorUserPosition = useMonitorUserPosition()
  const [trailArgs, trailOptions] =
    args.trailUuid && monitorUserPosition && userPositionOnTrail.lnglat
      ? [
          {
            trailUuid: args.trailUuid,
            start: lnglat2quadhash(
              userPositionOnTrail.lnglat[0],
              userPositionOnTrail.lnglat[1]
            ),
            stop: args.quadhash
          },
          { skip: options.skip }
        ]
      : [{}, { skip: true }]
  const trail =
    useGetActionResult(useCreateTrail, trailArgs, trailOptions) || {}
  const profile = useTrailProfile(
    { trailUuid: trail?.trail_uuid },
    { skip: !trail }
  )
  const userPositionOnConnection = useSpatialSearch(
    profile,
    userPositionOnTrail.lnglat,
    100
  )
  const needToReload =
    !!profile && !!userPositionOnTrail.lnglat && !userPositionOnConnection

  useEffect(() => {
    if (!needToReload) return
    setUserPosition(userPositionOnTrail.lnglat)
  }, [needToReload, userPositionOnTrail.lnglat])

  if (!profile || (!!userPositionOnTrail.lnglat && !userPositionOnConnection))
    return null

  const connectionPosition = profile[profile.length - 1]
  if (userPositionOnConnection) {
    return {
      distance: connectionPosition.distance - userPositionOnConnection.distance,
      time: connectionPosition.time - userPositionOnConnection.time,
      elevation: connectionPosition.elevation,
      distance_from_trail: 0
    }
  }
  return connectionPosition
}

export const useRelativeStats = (args) => {
  const {
    route_id,
    current_trail_id,
    quadhash,
    trail_id,
    checkpoint_position,
    start_length,
    stop_length,
    start_time,
    stop_time,
    distance_from_trail
  } = args
  const userPosition = useUserPosition()
  const route = useRoute({ routeId: route_id })
  const connection =
    useRealtiveTrailConnection(
      {
        trailUuid: route?.trails[trail_id - 1]?.uuid,
        quadhash: quadhash,
        position: checkpoint_position
      },
      {
        skip:
          !quadhash ||
          !userPosition ||
          !trail_id ||
          trail_id !== current_trail_id ||
          distance_from_trail < 250 ||
          !checkpoint_position
      }
    ) || {}

  if (!route) return {}

  let length
  let time

  if (trail_id === current_trail_id) {
    if (userPosition && distance_from_trail > 250 && checkpoint_position > 0) {
      length = connection.distance
      time = connection.time
    } else {
      switch (checkpoint_position) {
        case 0:
          length = start_length
          time = start_time
          if (userPosition) {
            length += userPosition.distance
            time += userPosition.time
          }
          break
        case -1:
          length = stop_length + route.trails[trail_id - 1].length
          time = stop_time + route.trails[trail_id - 1].time
          if (userPosition) {
            length -= userPosition.distance
            time -= userPosition.time
          }
          break
        default:
          length = start_length
          time = start_time
          if (userPosition) {
            length -= userPosition.distance
            time -= userPosition.time
          }
      }
    }
  } else {
    let offsetLength = 0
    let offsetTime = 0
    const trail_ids =
      current_trail_id < trail_id
        ? range(current_trail_id, trail_id - 1)
        : range(trail_id + 1, current_trail_id - 1)

    for (const next_trail_id of trail_ids) {
      const trail = route.trails[next_trail_id - 1]
      offsetLength += trail.length
      offsetTime += trail.time
    }

    if (trail_id > current_trail_id) {
      if (!userPosition) {
        length = offsetLength + start_length
        time = offsetTime + start_time
      } else {
        length = start_length + offsetLength - userPosition.distance
        time = start_time + offsetTime - userPosition.time
      }
    }

    if (trail_id < current_trail_id) {
      if (checkpoint_position === 0) {
        length = offsetLength + route.trails[trail_id].length + start_length
        time = offsetTime + route.trails[trail_id].time + start_time
      } else {
        if (!userPosition) {
          length = offsetLength + stop_length
          time = offsetTime + stop_time
        } else {
          length = stop_length + offsetLength + userPosition.distance
          time = stop_time + offsetLength + userPosition.time
        }
      }
    }
  }

  return {
    length: length !== undefined ? Math.abs(length) : undefined,
    time: time !== undefined ? Math.abs(time) : undefined,
    distance_from_trail: distance_from_trail,
    trail_id: trail_id
  }
}

export const useQuadhashStats = (args) => {
  const { quadhash, route_id, trail_id: current_trail_id } = args
  const { trail_id, distance_from_trail } =
    useRouteNearestTrail(
      {
        routeId: route_id,
        quadhash: quadhash
      },
      { skip: !quadhash || !current_trail_id }
    ) || {}
  const {
    checkpoint_position,
    start_length,
    stop_length,
    start_time,
    stop_time
  } =
    useTrailWaypointConnection(
      {
        routeId: route_id,
        trailId: trail_id,
        quadhash: quadhash
      },
      { skip: !trail_id }
    ) || {}
  return useRelativeStats({
    quadhash: quadhash,
    route_id: route_id,
    trail_id: trail_id,
    checkpoint_position: checkpoint_position,
    current_trail_id: current_trail_id,
    start_length: start_length,
    stop_length: stop_length,
    start_time: start_time,
    stop_time: stop_time,
    distance_from_trail: distance_from_trail
  })
}

export const useStepStats = (args) => {
  const { step, trail_id, route_id, current_trail_id } = args
  const {
    start_length,
    stop_length,
    start_time,
    stop_time,
    distance_from_trail,
    quadhash
  } = step || {}
  return useRelativeStats({
    quadhash: quadhash,
    route_id: route_id,
    trail_id: trail_id,
    checkpoint_position: null,
    current_trail_id: current_trail_id || trail_id,
    start_length: start_length,
    stop_length: stop_length,
    start_time: start_time,
    stop_time: stop_time,
    distance_from_trail: step?.is_deviation ? distance_from_trail : 0
  })
}

export const useOffsetTrailStatsToRoute = (args) => {
  const { route_id, trail_id, current_trail_id } = args
  const route = useRoute(
    { routeId: route_id },
    {
      skip:
        !trail_id || !current_trail_id || !current_trail_id === current_trail_id
    }
  )
  let length = 0
  let time = 0

  if (route) {
    if (current_trail_id < trail_id) {
      for (const next_trail_id of range(current_trail_id, trail_id - 1)) {
        const trail = route.trails[next_trail_id - 1]
        length += trail.length
        time += trail.time
      }
    }
    if (current_trail_id > trail_id) {
      for (const next_trail_id of range(trail_id + 1, current_trail_id - 1)) {
        const trail = route.trails[next_trail_id - 1]
        length += trail.length
        time += trail.time
      }
    }
  }
  return {
    offsetLength: length,
    offsetTime: time
  }
}

export const useRouteWaypointStats = (args) => {
  const { route_id, trail_id: current_trail_id, waypoint } = args
  const { quadhash } = waypoint || {}
  const userPosition = useUserPosition()
  const route = useRoute({ routeId: route_id })
  const {
    trail_id,
    position,
    start_distance,
    stop_distance,
    distance_from_trail
  } =
    useRouteWaypointConnection(
      {
        routeId: route_id,
        quadhash: quadhash
      },
      { skip: !quadhash || !current_trail_id }
    ) || {}
  const { offsetLength } =
    useOffsetTrailStatsToRoute({
      route_id: route_id,
      trail_id: trail_id,
      current_trail_id: current_trail_id
    }) || {}
  const connection =
    useRealtiveTrailConnection(
      {
        routeId: route_id,
        trailId: trail_id,
        quadhash: quadhash
      },
      {
        skip:
          !quadhash ||
          !userPosition ||
          !trail_id ||
          trail_id !== current_trail_id
      }
    ) || {}

  let length
  let time

  if (trail_id === current_trail_id) {
    if (!userPosition) {
      if (position === -1) {
        length = stop_distance + route.trails[trail_id - 1].length
      } else {
        length = start_distance
      }
    } else {
      length = connection.distance
    }
  }

  if (trail_id > current_trail_id) {
    if (!userPosition) {
      length = offsetLength + start_distance
    } else {
      length = start_distance + offsetLength - userPosition.distance
    }
  }

  if (trail_id < current_trail_id) {
    if (route) {
      if (position === 0) {
        length = offsetLength + route.trails[trail_id].length + start_distance
      } else {
        if (!userPosition) {
          length = offsetLength + stop_distance
        } else {
          length = stop_distance + offsetLength - userPosition.distance
        }
      }
    }
  }

  return {
    length: length,
    time: time,
    position: position,
    distance_from_trail: distance_from_trail
  }
}

export const useSetTrailStartAction = (args, options) => {
  const [action, isLoading] = useNavigateToActionResult(
    useSetRouteTrailStart,
    options
  )
  return {
    text: SET_STAGE_START,
    onClick: (e) => action(args),
    isLoading: isLoading
  }
}

export const useSetTrailStopAction = (args, options) => {
  const [action, isLoading] = useNavigateToActionResult(
    useSetRouteTrailStop,
    options
  )
  return {
    text: SET_STAGE_STOP,
    onClick: (e) => action(args),
    isLoading: isLoading
  }
}

export const useSplitTrailAction = (args, options) => {
  const [action, isLoading] = useNavigateToActionResult(
    useSplitRouteTrail,
    options
  )
  return {
    text: SPLIT_STAGE,
    onClick: (e) => action(args),
    isLoading: isLoading
  }
}

export const useRemoveTrailCheckpointAction = (args, options) => {
  const [action, isLoading] = useNavigateToActionResult(
    useRemoveRouteCheckpoint,
    options
  )
  return {
    text: REMOVE_STOP,
    onClick: (e) => action(args),
    isLoading: isLoading
  }
}

export const useAddTrailCheckpointAction = (args, options) => {
  const [action, isLoading] = useNavigateToActionResult(
    useAddRouteCheckpoint,
    options
  )
  switch (args.position) {
    case 0:
      return {
        text: ADD_AT_TRAIL_START,
        onClick: (e) => action(args),
        isLoading: isLoading
      }
    case -1:
      return {
        text: ADD_AT_TRAIL_END,
        onClick: (e) => action(args),
        isLoading: isLoading
      }
    default:
      return {
        text: ADD_TO_TRAIL,
        onClick: (e) => action(args),
        isLoading: isLoading
      }
  }
}

export const useReservationActions = (args, options = {}) => {
  const settings = useContext(SettingsContext)
  const history = useHistory()
  const dispatch = useDispatch()
  if (options.skip) return []
  const reverse = settings.URLS.reverse
  const url = reverse.apply(null, args)
  const handler = (e) => {
    history.push(
      {
        pathname: url,
        hash: history.location.hash
      },
      Object.assign({}, history.location.state, { dirty: true })
    )
    dispatch(showSidebar('reservation'))
  }
  return [
    {
      text: CHECK_AVAILABILITY_PRICE,
      onClick: handler,
      className: 'primary',
      pinned: true,
      isLoading: false
    }
  ]
}

export const useQuadhashActions = (args, options = {}) => {
  const {
    route_id,
    trail_id,
    quadhash,
    checkpoint_position,
    start_length,
    stop_length,
    isStopPoint,
    distance_from_trail,
    current_trail_id
  } = args
  const route = useRoute({ routeId: route_id })
  const is_next_trail = current_trail_id && trail_id === current_trail_id + 1
  const is_prev_trail = current_trail_id && trail_id === current_trail_id - 1
  const is_same_trail = trail_id === current_trail_id || !current_trail_id

  const setTrailStartAction = useSetTrailStartAction(
    {
      routeId: route_id,
      trailId:
        is_prev_trail && checkpoint_position == 0
          ? trail_id
          : current_trail_id || trail_id,
      quadhash: quadhash
    },
    {
      reverse_defaults: {
        trail_id: current_trail_id
      }
    }
  )
  const setTrailStopAction = useSetTrailStopAction(
    {
      routeId: route_id,
      trailId:
        is_next_trail && checkpoint_position == -1
          ? trail_id
          : current_trail_id || trail_id,
      quadhash: quadhash
    },
    {
      reverse_defaults: {
        trail_id: current_trail_id
      }
    }
  )
  const addTrailChekpointAction = useAddTrailCheckpointAction(
    {
      routeId: route_id,
      trailId: trail_id,
      quadhash: quadhash,
      position: checkpoint_position
    },
    {
      reverse_defaults: {
        trail_id: current_trail_id
      }
    }
  )

  const splitTrailAction = useSplitTrailAction(
    {
      routeId: route_id,
      trailId: trail_id,
      quadhash: quadhash,
      position: checkpoint_position
    },
    {
      reverse_defaults: {
        trail_id: current_trail_id
      }
    }
  )

  if (!route || options.skip || checkpoint_position === undefined) return null

  const actions = []
  const trail = route.trails[trail_id - 1]

  if (isStopPoint && start_length > 0 && stop_length > 0) {
    const split_length = Math.min(7000, Math.max(3000, trail.length / 3))

    if (
      checkpoint_position > 0 &&
      start_length > split_length &&
      stop_length > split_length
    ) {
      actions.push(splitTrailAction)
    }

    const limit = trail.length / 1.5
    if (start_length < limit) {
      if (is_next_trail) {
        actions.push(setTrailStopAction)
      }
      if (is_same_trail || is_prev_trail) {
        actions.push(setTrailStartAction)
      }
    } else if (stop_length < limit) {
      if (is_prev_trail) {
        actions.push(setTrailStartAction)
      }
      if (is_same_trail) {
        actions.push(setTrailStopAction)
      }
    }
  }

  if (
    (checkpoint_position > 0 &&
      stop_length > 0 &&
      distance_from_trail > 50 &&
      start_length > 0) ||
    (checkpoint_position === 0 && trail_id === 1) ||
    (checkpoint_position === -1 && trail_id === route.trails.length)
  )
    actions.push(addTrailChekpointAction)

  return actions
}

export const useStepActions = (args, options = {}) => {
  const { step, route_id, trail_id, current_trail_id } = args
  const {
    quadhash,
    amenities,
    step_id,
    start_length,
    stop_length,
    is_start_stop,
    is_checkpoint
  } = step || {}
  const route = useRoute({ routeId: route_id }, options)
  const steps = useSteps(
    { routeId: route_id, trailId: trail_id },
    { skip: !trail_id || options.skip }
  )
  const trail = route && route.trails[trail_id - 1]
  const isStopPoint =
    !is_start_stop && amenities && 'accommodation' in amenities
  const is_next_trail = current_trail_id && trail_id === current_trail_id + 1
  const is_prev_trail = current_trail_id && trail_id === current_trail_id - 1
  const is_same_trail = trail_id === current_trail_id || !current_trail_id

  let checkpoint_position
  if (steps && step_id) {
    checkpoint_position = 0
    for (const s of steps) {
      if (s.step_id === step_id) {
        break
      }
      if (s.is_checkpoint) {
        checkpoint_position += 1
      }
    }
  }

  const setTrailStartAction = useSetTrailStartAction(
    {
      routeId: route_id,
      trailId: current_trail_id || trail_id,
      quadhash: quadhash
    },
    {
      reverse_defaults: {
        trail_id: current_trail_id
      }
    }
  )

  const setTrailStopAction = useSetTrailStopAction(
    {
      routeId: route_id,
      trailId: current_trail_id || trail_id,
      quadhash: quadhash
    },
    {
      reverse_defaults: {
        trail_id: current_trail_id
      }
    }
  )

  const removeTrailCheckpointAction = useRemoveTrailCheckpointAction(
    {
      routeId: route_id,
      trailId: trail_id,
      position: checkpoint_position
    },
    {
      reverse_defaults: {
        trail_id: route
          ? current_trail_id === trail_id &&
            trail_id === route.trails.length &&
            is_start_stop
            ? current_trail_id - 1
            : current_trail_id
          : null
      }
    }
  )

  const addTrailChekpointAction = useAddTrailCheckpointAction(
    {
      routeId: route_id,
      trailId: trail_id,
      quadhash: quadhash,
      position: checkpoint_position
    },
    {
      reverse_defaults: {
        trail_id: current_trail_id
      }
    }
  )

  const splitTrailAction = useSplitTrailAction(
    {
      routeId: route_id,
      trailId: trail_id,
      quadhash: quadhash,
      position: checkpoint_position
    },
    {
      reverse_defaults: {
        trail_id: current_trail_id
      }
    }
  )

  if (!trail_id || !route || !steps || options?.skip) {
    return null
  }

  const actions = []

  if (is_checkpoint) {
    const trailChekpoints = steps.filter((x) => x.is_checkpoint)
    if (route.trails.length > 1) {
      if (trail_id === 1 && checkpoint_position === 0) {
        removeTrailCheckpointAction.text = REMOVE_FIRST_STAGE
      } else if (
        trail_id === route.trails.length &&
        checkpoint_position === trailChekpoints.length - 1
      ) {
        removeTrailCheckpointAction.text = REMOVE_LAST_STAGE
      } else if (checkpoint_position === trailChekpoints.length - 1) {
        removeTrailCheckpointAction.text = MERGE_STAGE
      }
    }

    if (trailChekpoints.length > 2 && checkpoint_position === 0) {
      removeTrailCheckpointAction.text = REMOVE_START_POINT
    }
    actions.push(removeTrailCheckpointAction)
  }

  if (isStopPoint && start_length > 0 && stop_length > 0) {
    const split_length = Math.min(7000, Math.max(3000, trail.length / 3))
    if (
      checkpoint_position > 0 &&
      start_length > split_length &&
      stop_length > split_length
    ) {
      actions.push(splitTrailAction)
    } else {
      const limit = trail.length / 1.5
      if (start_length < limit) {
        if (is_next_trail) {
          actions.push(setTrailStopAction)
        }
        if (is_same_trail || is_prev_trail || !current_trail_id) {
          actions.push(setTrailStartAction)
        }
      } else if (stop_length < limit) {
        if (is_prev_trail) {
          actions.push(setTrailStartAction)
        }
        if (is_same_trail || is_next_trail) {
          actions.push(setTrailStopAction)
        }
      }
    }
  }

  if (step?.is_deviation) {
    actions.push(addTrailChekpointAction)
  }

  return actions
}

export const useElementSize = (debounceMs = 150) => {
  const ref = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let timer = null

    const observer = new ResizeObserver(([entry]) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        const { width, height } = entry.contentRect
        setSize({ width, height })
      }, debounceMs)
    })

    observer.observe(el)
    return () => {
      observer.disconnect()
      clearTimeout(timer)
    }
  }, [debounceMs])

  return { ref, size }
}

export default useElementSize
